import { logError, parseSearchParams } from ".";
import URI from "urijs";
import { Location } from "history";
import { UserLanguage } from "../@types";

/**
 * Fix subdomain for language-specific Medicare URL
 * @param url
 * @param langCode
 * @returns - Language-updated URL
 */
export const getLanguageSpecificMedicareUrl = (
  url: string,
  langCode = "en"
): string => {
  const medicareTld = "medicare.gov";
  // If we're serving English or this isn't a Medicare URL, just return the URL
  if (langCode === "en" || !url.includes(medicareTld)) {
    return url;
  }
  // Update the portion of the URL with or without a subdomain to include
  // the language subdomain
  const regex = new RegExp(`([a-z]*\\.?)(${medicareTld})`);
  return url.replace(regex, `${langCode}.$2`);
};

export const parseLangFromRelayParam = (relay = ""): UserLanguage | null => {
  try {
    // relay should be a base64-encoded string
    const { lang }: { lang: string } = JSON.parse(atob(relay));
    // Get valid values from UserLanguage enum
    if (!Object.values(UserLanguage).find(val => lang === val)) {
      throw new Error(
        "Encoded 'lang' value in URL's 'relay' param is not a valid UserLanguage"
      );
    }
    return lang as UserLanguage;
  } catch (e) {
    logError(
      "Failed to parse the relay (languageHelpers.parseLangFromRelayParam)",
      e as Error
    );
    return null;
  }
};

export const getLanguageFromLocationOrState = ({
  location: { search },
  stateLang,
}: {
  location: Location;
  stateLang: UserLanguage;
}): UserLanguage => {
  const { relay, lang: searchLang } = parseSearchParams(URI.parseQuery(search));
  let language = stateLang;
  if (searchLang && searchLang !== stateLang) {
    language = searchLang as UserLanguage;
  }
  if (relay) {
    const relayLang = parseLangFromRelayParam(relay);
    language = relayLang || language;
  }
  return language;
};
